"use client";

import { ArrowUpRight, Smartphone, X } from "lucide-react";
import React, { useSyncExternalStore } from "react";

import { IconButton } from "@lattice/frontend/components/kit/IconButton";
import { useDevicePlatform } from "@lattice/frontend/hooks/use-device-platform";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
} from "@lattice/frontend/lib/constants/app-store-urls";
import "./mobile-app-download-banner.css";

export const MOBILE_APP_DOWNLOAD_DISMISSAL_STORAGE_KEY =
  "lattice:mobile-app-download-banner:dismissed:v1";
const dismissalListeners = new Set<() => void>();
let dismissedInMemory = false;
let dismissalStorageUnavailable = false;

export type T_MobileAppPlatform = "android" | "ios";

function readDismissed(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const dismissedInStorage =
      window.localStorage.getItem(MOBILE_APP_DOWNLOAD_DISMISSAL_STORAGE_KEY) ===
      "true";
    return (
      dismissedInStorage || (dismissalStorageUnavailable && dismissedInMemory)
    );
  } catch {
    return dismissedInMemory;
  }
}

function subscribeToDismissal(listener: () => void): () => void {
  dismissalListeners.add(listener);
  const handleStorage = (event: StorageEvent) => {
    if (event.key === MOBILE_APP_DOWNLOAD_DISMISSAL_STORAGE_KEY) listener();
  };
  window.addEventListener("storage", handleStorage);
  return () => {
    dismissalListeners.delete(listener);
    window.removeEventListener("storage", handleStorage);
  };
}

function dismissBanner(): void {
  dismissedInMemory = true;
  try {
    window.localStorage.setItem(
      MOBILE_APP_DOWNLOAD_DISMISSAL_STORAGE_KEY,
      "true",
    );
    dismissalStorageUnavailable = false;
  } catch {
    dismissalStorageUnavailable = true;
    // In-memory dismissal still keeps the banner closed for this page lifetime.
  }
  for (const listener of dismissalListeners) listener();
}

export type T_MobileAppDownloadBannerViewProps = {
  onDismiss: () => void;
  platform: T_MobileAppPlatform;
};

/** Pure visual surface used by production and deterministic Storybook states. */
export function MobileAppDownloadBannerView({
  onDismiss,
  platform,
}: T_MobileAppDownloadBannerViewProps) {
  const isIos = platform === "ios";
  const storeUrl = isIos ? APP_STORE_URL : PLAY_STORE_URL;
  const platformName = isIos ? "iOS" : "Android";

  return (
    <aside
      aria-label="Get the Lattice mobile app"
      className="mobile-app-download-banner"
    >
      <Smartphone
        aria-hidden
        className="mobile-app-download-banner__device"
        size={18}
      />
      <div className="mobile-app-download-banner__copy">
        <p className="mobile-app-download-banner__title">Lattice mobile app</p>
        <p className="mobile-app-download-banner__subtitle">
          Download Lattice for {platformName}.
        </p>
      </div>
      <a
        aria-label={`Download Lattice for ${platformName}`}
        className="mobile-app-download-banner__cta"
        href={storeUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        Get app
        <ArrowUpRight aria-hidden size={14} />
      </a>
      <IconButton
        ariaLabel="Dismiss app download banner"
        icon={<X aria-hidden size={16} />}
        onClick={onDismiss}
        size="compact"
        variant="quiet"
      />
    </aside>
  );
}

export function MobileAppDownloadBanner({
  platform,
}: {
  platform: T_MobileAppPlatform;
}) {
  const dismissed = useSyncExternalStore(
    subscribeToDismissal,
    readDismissed,
    () => true,
  );
  if (dismissed) return null;
  return (
    <MobileAppDownloadBannerView
      onDismiss={dismissBanner}
      platform={platform}
    />
  );
}

/** Global slot for layouts that do not already resolve device platform. */
export function DetectedMobileAppDownloadBanner() {
  const platform = useDevicePlatform();
  if (platform !== "ios" && platform !== "android") return null;
  return <MobileAppDownloadBanner platform={platform} />;
}
